import { useState } from 'react';
import type { MpParticipant } from '@accounting/shared';
import { useCompanies } from '../../hooks/useCompanies';
import { useCreateManualParticipationSession } from '../../hooks/useParticipation';
import { ApiError } from '../../lib/api';

// Work the phone cannot see: bookkeeping at home, phone calls, a visit where
// the phone stayed in the car. It counts like any other session and is labeled
// MANUAL everywhere it appears.
export function ManualSessionForm({ onDone }: { onDone: () => void }) {
  const companies = useCompanies();
  const create = useCreateManualParticipationSession();
  const [companyId, setCompanyId] = useState('');
  const [participant, setParticipant] = useState<MpParticipant>('self');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [note, setNote] = useState('');
  const [workCategory, setWorkCategory] = useState('');
  const [travel, setTravel] = useState('0');
  const [formError, setFormError] = useState<string | null>(null);

  const save = () => {
    setFormError(null);
    const startAt = start ? new Date(start) : null;
    const endAt = end ? new Date(end) : null;
    if (!companyId) return setFormError('Choose the business the time was for.');
    if (!startAt || !endAt || Number.isNaN(startAt.getTime()) || Number.isNaN(endAt.getTime())) {
      return setFormError('Enter both a start and an end.');
    }
    if (endAt <= startAt) return setFormError('The end must be after the start.');
    if (!note.trim()) return setFormError('Say what the work was. A manual entry with no description proves little.');
    const travelMinutes = Number(travel || '0');
    if (!Number.isInteger(travelMinutes) || travelMinutes < 0 || travelMinutes > 600) {
      return setFormError('Travel must be a whole number of minutes from 0 to 600.');
    }
    create.mutate(
      {
        companyId,
        participant,
        startAt: startAt.toISOString(),
        endAt: endAt.toISOString(),
        travelMinutes,
        note: note.trim(),
        workCategory: workCategory.trim() || null,
      },
      { onSuccess: onDone },
    );
  };

  const apiError =
    create.error instanceof ApiError
      ? ((create.error.body as { error?: string } | null)?.error ?? 'Save failed')
      : create.error?.message;

  return (
    <div className="mp-editor">
      <div className="mp-editor__head">
        <strong>Add time by hand</strong>
        <span className="tag warn">manual</span>
      </div>
      <div className="mp-editor__grid">
        <label htmlFor="mp-m-company">Business</label>
        <select id="mp-m-company" value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
          <option value="">Choose…</option>
          {(companies.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <label htmlFor="mp-m-who">Who</label>
        <select id="mp-m-who" value={participant} onChange={(e) => setParticipant(e.target.value as MpParticipant)}>
          <option value="self">Self</option>
          <option value="spouse">Spouse</option>
        </select>
        <label htmlFor="mp-m-start">Start</label>
        <input id="mp-m-start" type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} />
        <label htmlFor="mp-m-end">End</label>
        <input id="mp-m-end" type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} />
        <label htmlFor="mp-m-travel">Travel (min)</label>
        <input
          id="mp-m-travel"
          type="text"
          inputMode="numeric"
          value={travel}
          onChange={(e) => setTravel(e.target.value)}
          style={{ maxWidth: 120 }}
        />
        <label htmlFor="mp-m-note">What you did</label>
        <input id="mp-m-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
        <label htmlFor="mp-m-category">Work category</label>
        <input
          id="mp-m-category"
          value={workCategory}
          onChange={(e) => setWorkCategory(e.target.value)}
          maxLength={120}
          placeholder="(optional)"
        />
      </div>
      {(formError ?? apiError) && <div className="field-error">{formError ?? apiError}</div>}
      <div className="mp-editor__actions">
        <button className="btn-primary" onClick={save} disabled={create.isPending}>
          {create.isPending ? 'Saving…' : 'Add time'}
        </button>
        <button className="btn-secondary" onClick={onDone} disabled={create.isPending}>
          Cancel
        </button>
      </div>
    </div>
  );
}
